import {autoinject, transient, computedFrom, observable, BindingEngine, PLATFORM, useView} from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { DataType, SourceType, QueryFilter } from "../../../../../../models/queries/queries.models";
import { Claims } from "../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { DialogServiceConstants } from "../../../../../../constants/basics/dialog.constants";
import { ManageRolesClientService } from "../../../../../../services/manage/manage-roles.service";
import { DeleteRoleRequest } from "../../../../../../models/manage/roles.models";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { MenuItem } from "../../../../../components/menu/menu-item";
import { RightshipConfirmationDialogModel } from "../../../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { PermissionEntity } from "../../../../../../models/manage/permissions.models";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";

@autoinject
@transient()
@useView('views/pages/modules/customers/steps/roles/roles.html')
export class CustomerRoles extends CustomGridPageBase  {

    @observable
    public branchId: number;

    private canAccessPermissions = false;
    private canManageRoles = false;
    private canManageLockedRoles = false;
    public isDataSubmission = false;

    public departments: number[];

    /**
     *
     */
    constructor(protected i18n: I18N,protected bindingEngine: BindingEngine, private userClaimsService: UserClaimsService, private manageRolesClientService: ManageRolesClientService,
        private dialogClientService: DialogClientService, private lookupsClientService: LookupClientService, 
        private notificationClientService: NotificationClientService) {
            super(i18n, "Roles", bindingEngine)
        this.canAccessPermissions = this.userClaimsService.hasPermission(Claims.CanAccessPermissions);
        this.canManageRoles = this.userClaimsService.hasPermission(Claims.CanManageRoles);
        this.canManageLockedRoles = this.userClaimsService.hasPermission(Claims.CanManageLockedRoles);
    }

    activate(model) {
        this.isDataSubmission = model && model.isDataSubmission;
    }

    branchIdChanged() {
        this.reload();
    }

    reload() {

        const request = new LookupValueRequest();
        request.lookupValueQuery = "departments";
        request.parameters = this.branchId ? this.branchId.toString() : "";
        this.lookupsClientService.getLookup(request, 'default').then(x => {
            this.departments = x.activeValues.map(x => x.id);

            const filters = [];

            this.departments.forEach(x => {
                filters.push(QueryFilter.getQuery("departmentId", x, 'eq', DataType.Number, true));
            });

            filters.push(QueryFilter.getQuery("isActive", true, "eq", DataType.Boolean, true));

            this.itemSource = {
                columns: [
                    {
                        sortOrder: 1,
                        columnOrder: 1,
                        name: "roleName",
                        dataType: DataType.CustomTemplate,
                        instancePerRow: true,
                        title: this.i18n.tr('role-name-text'),
                        isFilterable: false,
                        value: `<div class="role-name row align-items-middle">
                            <span>\${item.roleName}</span>
                            <template if.bind="item.description">
                                <comp-icon ref="roleNameInfoIcon" class="info-icon" icon="info"></comp-icon>
                                <comp-tooltip on-element.bind="roleNameInfoIcon" position="top-left" show-arrow="true">
                                    <pre innerhtml.bind="item.description | htmlSanitize"></pre>
                                </comp-tooltip>
                            </template>
                            <comp-icon class="lock-icon" icon="lock" if.bind="item.isLocked"></comp-icon>
                        </div>`,
                        parameter: {
                            item: null
                        }
                    },
                    {
                        columnOrder: 99,
                        sortOrder: 99,
                        dataType: DataType.CustomTemplate,
                        instancePerRow: true,
                        name: "menu",
                        value: `<div class="actions row justify-content-end align-items-middle" if.bind="!item.isLocked || (item.isLocked && canManageLockedRoles)">
                                    <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" show.bind="isVisible">
                                        <template replace-part="menu-item">
                                            <div class="row nowrap align-items-middle">
                                                <span class="text">\${menuItem.item.text}</span>
                                            </div>  
                                        </template>
                                    </list-menu>
                                </div>`,
                        parameter: {
                            item: null,
                            canManageLockedRoles: this.canManageLockedRoles,
                            isVisible: (this.canManageRoles || this.canAccessPermissions),
                            menuItems: (item) => 
                            {
                                const menuItems: MenuItem[] = [];
                        
                                menuItems.push({isVisible: this.canManageRoles, click: () => this.editRole(item.roleId), item: { text: "Edit" }, classList: ''});
                                menuItems.push({isVisible: this.canManageRoles, click: () => this.cloneRole(item.roleId), item: { text: "Clone" }, classList: ''});
                                menuItems.push({isVisible: this.canAccessPermissions, click: () => this.editPermissions(item.roleId, item.roleName), item: { text: "Permissions" }, classList: ''});
                                menuItems.push({isVisible: this.canManageRoles && !this.isDataSubmission, click: () => this.removeRole(item.roleId), item: { text: "Remove" }, classList: ''});
                                menuItems.push({isVisible: true, click: () => this.toggleRoleActivation(item), item: { text: item.isActive ? "Deactivate" : 'Activate' }, classList: ''});

                                return menuItems;
                            }
                        }
                    }
                ],
                source: "/odata/RightShipQiRoles",
                sourceType: SourceType.ODataUrl,
                initialFilters: filters
            }
        });


    }

    public add(){
        this.dialogClientService.show(PLATFORM.moduleName('views/pages/modules/customers/dialogs/role-dialog'), { view: this }, true, false, false, 'right');
    }

    @computedFrom('itemSource')
    public get hasManageRolesPermission(): boolean {
        if (!this.userClaimsService) return false;
        return this.userClaimsService.hasPermission(Claims.CanManageRoles);
    }

    deleteRole(roleId: number) {
        return new Promise<void>((resolve, reject) => {
            const request: DeleteRoleRequest = {
                roleId: roleId
            }
            
            this.manageRolesClientService.deleteRole(request).then(() => {
                this.notificationClientService.show(this.i18n.tr('successfully-deleted-text'), "", NotificationType.Success);
                this.reload()
                resolve();
            }).catch(x => {
                const response: {
                    exception: string;
                    details: string;
                } = typeof(x.response) === 'object'
                    ? x.response
                    : JSON.parse(x.response);
                if (!response.exception || !response.details) {
                    this.notificationClientService.show(this.i18n.tr('failed-role-deletion'), "", NotificationType.Error);
                }
                reject();
            });
        })
    }

    public editRole = (roleId: number) => {
        this.dialogClientService.show(PLATFORM.moduleName('views/pages/modules/customers/dialogs/role-dialog'), { view: this, id: roleId }, true, false, false, 'right');
    }

    public cloneRole = (roleId: number) => {
        this.dialogClientService.show(PLATFORM.moduleName('views/pages/modules/customers/dialogs/clone-role-dialog'), { view: this, id: roleId }, true, false, false, 'right');
    }

    public editPermissions = (roleId: number, roleName: string) => {
        this.dialogClientService.show(PLATFORM.moduleName("views/pages/modules/customers/dialogs/permissions-dialog"), {
            type: PermissionEntity.Role,
            id: roleId,
            name: roleName,
            showCloseButton: true,
            cancelOperation: () => { this.dialogClientService.hide(); },
            afterSaveOperation: () => { this.dialogClientService.hide(); },
         }, true, false, false, 'right');    
    }

    public removeRole = (roleId: number) => {        
        return new Promise((resolve, reject) => {
            const model = new RightshipConfirmationDialogModel();
            model.confirm = this.i18n.tr('delete-text');
            model.cancel = this.i18n.tr('cancel-text');
            model.messages.push(this.i18n.tr('are-you-sure-you-want-to-delete'));
            model.title = this.i18n.tr('confirm-delete-text');
            model.onConfirm = () => this.deleteRole(roleId).then(resolve).catch(reject),
            model.onCancel = () => {
                reject();
            },
            this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, true, false, false, 'right');
        });
    }

    public toggleRoleActivation = (role) => {
        return new Promise((resolve, reject) => {

            const isActivating = !role.isActive;

            const model = new RightshipConfirmationDialogModel();
            model.cancel = this.i18n.tr('cancel-text');
            model.title = isActivating ? 'Confirm Activating Role' : 'Confirm Deactivate Role?';

            if (!isActivating) {

                if (role.activeUsersCount) {
                    model.messages.push('Role cannot be deactivated because active users are currently associated with the role <b>' + role.roleName + '</b>.');
                }
                else {
                    model.confirm = 'Deactivate';
                    model.messages.push('No user is currently associated to the role <b>' + role.roleName + '</b>');    
                }                
            }

            model.onConfirm = () => {      
                return new Promise((resolve, reject) => {          
                    this.manageRolesClientService.changeRoleActivationState({id: role.roleId, authorizeDeactivation: true }).then(x => {
                        this.notificationClientService.show("Successfully " + (isActivating ? "Activated" : "Deactivated"), "", NotificationType.Success, 5000);
                        this.reload()
                        resolve(x);
                    }).catch(() => {
                        this.notificationClientService.show("Failed to " + (isActivating ? "Activate" : "Deactivate"), "", NotificationType.Error, 5000);  
                        reject();
                    });
                });
            },
            model.onCancel = () => {
                reject();
            },
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.DeactivateRoleDialog, model, true, false, false, 'right');
        });
    }
}